import { Descriptions, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type WhatsappLiveChatModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/WhatsappLiveChatModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import WhatsappLiveChatModuleActions from './WhatsappLiveChatModuleActions';
import WhatsappLiveChatSettingsEmptyList from './WhatsappLiveChatSettingsEmptyList';
import WhatsappLiveChatSettingsTable from './WhatsappLiveChatSettingsTable';

export type WhatsappLiveChatModulePageProps = { module: WhatsappLiveChatModuleWithPermissionsSpecsFragment };

const WhatsappLiveChatModulePage = ({ module }: WhatsappLiveChatModulePageProps) => {
    const { t } = useTranslation(['whatsappLiveChatModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('whatsappLiveChatModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('whatsappLiveChatModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<WhatsappLiveChatModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md ? t('whatsappLiveChatModuleDetails:title', { name: module.displayName }) : module.displayName
            }
        >
            <Divider>{t('whatsappLiveChatModuleDetails:dividers.mainDetails')}</Divider>
            {module.liveChatSettings.length === 0 ? (
                <WhatsappLiveChatSettingsEmptyList module={module} />
            ) : (
                <WhatsappLiveChatSettingsTable module={module} />
            )}
        </ConsolePageWithHeader>
    );
};

export default WhatsappLiveChatModulePage;
